export const cleanEnvVar = (value: string | undefined): string => {
  if (!value || value === 'undefined') return '';
  return value.replace(/^["']|["']$/g, ''); // Remove leading/trailing quotes
};

// Safely access environment variables for browser compatibility
export const getEnvVar = (key: string): string | undefined => {
  // Vite exposes env vars on `import.meta.env`
  // @ts-ignore
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
    // @ts-ignore
    return import.meta.env[key];
  }
  // Check for process.env for Node.js environments (e.g., tests, backend)
  if (typeof process !== 'undefined' && process.env && process.env[key]) {
    return process.env[key];
  }
  return undefined;
};

// Environment variables with fallbacks and quote cleaning
const rawAppApi = getEnvVar('VITE_APP_API');
const cleanedAppApi = cleanEnvVar(rawAppApi);
const backendApi = cleanEnvVar(getEnvVar('VITE_WORDPRESS_API')) || 'http://localhost:8000/wp-json/ideoz/v1';
const platformUrl = cleanEnvVar(getEnvVar('VITE_PLATFORM_LINK')) || 'http://localhost:3000';
const appApi = cleanedAppApi || 'http://localhost:8001/';
const aiApi = cleanEnvVar(getEnvVar('VITE_AI_API')) || 'http://localhost:8002/';
const platformLink = cleanEnvVar(getEnvVar('VITE_PLATFORM_LINK')) || 'https://ideoz.ai/';

// Log environment variables in development for debugging
const isDev = getEnvVar('VITE_NODE_ENV') === 'development' || getEnvVar('NODE_ENV') === 'development';
if (isDev) {
  console.log('🔧 API Configuration:', {
    rawAppApi,
    cleanedAppApi,
    appApi,
    backendApi,
    platformUrl,
    aiApi,
    platformLink,
    buildTarget: getEnvVar('VITE_BUILD_TARGET'),
    nodeEnv: getEnvVar('VITE_NODE_ENV') || getEnvVar('NODE_ENV'),
  });
}

// Note: All API calls automatically handle 401 errors through the global interceptor
// which will logout users and clear their session when unauthorized responses are received
export const ApiUrls = {
  // Platform Links
  platformLink: platformLink,

  // Auth endpoints
  login: `${appApi}auth/login`,
  register: `${appApi}auth/register`,
  socialLogin: `${appApi}auth/social-login`,
  generatePairingCode: `${appApi}auth/generate-pairing-key`,
  pairingCode: `${appApi}auth/pairing-key-access-token`,
  pairLoggedUser: `${appApi}auth/pair-logged-user`,
  platformUrl: platformUrl,
  userProfile: `${appApi}auth/get-user-data`,
  updateOnboardingShown: `${appApi}auth/update-onboarding-shown`,
  updateGracePeriodNoticeShown: `${appApi}auth/grace-period-notice-shown`,
  logoutUser: `${appApi}auth/logout`,
  refreshToken: `${appApi}auth/refresh`,
  forgotPassword: `${appApi}auth/forgot-password`,
  resetPassword: `${appApi}auth/reset-password`,
  createProject: `${appApi}projects`,
  createSampleProject: `${appApi}projects/sample/portfolio`,
  getProject: (projectId: string) => `${appApi}projects/${projectId}`,
  updateProject: (projectId: string) => `${appApi}projects/${projectId}`,
  deleteProject: (projectId: string) => `${appApi}projects/${projectId}`,
  getUserProjectsWithPagination: (limit: number = 50, page: number = 1) => {
    return `${appApi}projects?limit=${limit}&page=${page}`;
  },
  checkSiteAvailability: `${backendApi}/check-site-availability`,
  addEvent: `${appApi}metrics/add-event`,
  addAiEvent: `${appApi}metrics/add-ai-event`,
  addFeedback: `${appApi}metrics/add-feedback`,
  changePassword: `${appApi}auth/change-password`,
  addHint: `${appApi}metrics/add-hint`,
  hints: `${backendApi}/hints`,
  promptHints: `${appApi}prompt-hints/prompt-hint`,
  getPromptCode: `${aiApi}/prompt-code`,
  addTime: `${backendApi}/metrics/add-time`,
  userIP: `${backendApi}/metrics/add-visit`,
  getPoints: `${backendApi}/transactions/get-user-balance`,
  addTransaction: `${backendApi}/transactions/add-transaction`,
  chat: `${aiApi}/llm-interaction`,
  chats: `${appApi}chats`,
  tmpConversations: `${appApi}tmp-conversations`,
  projectConversations: (projectId: string) => `${appApi}projects/${projectId}/conversations`,
  conversations: (conversationId: string) => `${appApi}conversations/${conversationId}/llm-interactions`,
  tmpConversationsLLM: (conversationId: string) => `${appApi}tmp-conversations/${conversationId}/tmp-llm-interactions`,
  tmpConversationsInteractions: (conversationId: string) => `${appApi}tmp-conversations/${conversationId}/tmp-llm-interactions`,
  // Like endpoints for LLM interactions
  tmpLlmInteractionLike: (tmpLlmInteractionId: string) => `${appApi}tmp-conversations/tmp-llm-interactions/${tmpLlmInteractionId}/like`,
  llmInteractionLike: (llmInteractionId: string) => `${appApi}conversations/llm-interactions/${llmInteractionId}/like`,
  tokens: (id: string) => `${aiApi}/llm-interaction/${id}/tokens`,
  saveUserData: `${appApi}auth/change-user-data`,
  requestCost(requestId: string) {
    return `${aiApi}/request-cost/${requestId}`
  },
  requestUpgrade: `${appApi}auth/request-upgrade`,
  acceptTerms: `${backendApi}/auth/accept-terms`,
  benchmark: `${aiApi}/benchmark-evaluation`,
  AiTranslation: `${aiApi}/translation`,
  deletionReasons: `${backendApi}/auth/get-settings`,
  deleteAccount: `${backendApi}/auth/delete-user`,
  checkVisitor: `${appApi}visitors/check`,
  projectMetadata: () => `${appApi}projects/metadata`,
  chatAttachments: `${appApi}attachments/chat-attachments/`,
  // File upload endpoints
  uploadProjectFiles: (projectId: string) => `${appApi}projects/${projectId}/attachments`,
  getProjectAttachments: (projectId: string) => `${appApi}projects/${projectId}/attachments`,
  deleteProjectAttachment: (fileId: string) => `${appApi}attachments/${fileId}`,

  // Drawing endpoints
  tmpConversationDrawing: (conversationId: string) => `${appApi}tmp-conversations/${conversationId}/drawing`,
  projectConversationDrawing: (projectId: string, conversationId: string) => `${appApi}projects/${projectId}/conversations/${conversationId}/drawing`,

  // Prototype endpoints
  tmpConversationPrototype: (conversationId: string) => `${appApi}tmp-conversations/${conversationId}/prototype`,
  projectConversationPrototype: (projectId: string, conversationId: string) => `${appApi}projects/${projectId}/conversations/${conversationId}/prototype`,
  updatePrototyperDemoLink: (id: number) => `${appApi}prototyper/${id}`,
  getPrototyperBySlug: (slug: string) => `${appApi}prototyper/by-slug/${slug}`,
  getPrototyperById: (id: string | number) => `${appApi}prototyper/${id}`,

  conversationsById: (id: string) => `${appApi}conversations/${id}`,
  addUserAction: `${appApi}auth/add-user-action`,
  cleanUserActions: `${appApi}auth/clean-user-actions`,
  newEvent: `${backendApi}/metrics/add-new-event`,

  // Analytics events endpoint (new system)
  events: `${appApi}events`,
  // Bug reports
  submitBugReport: `${appApi}bug-reports`,

  // Google Drive endpoints
  gdriveAuthStatus: `${appApi}gdrive/auth/status`,
  gdriveAuthUrl: (redirectUrl: string) => `${appApi}gdrive/auth/auth-url?redirectUrl=${redirectUrl}`,
  gdriveOAuthCallback: (code: string, redirectUrl: string) =>
    `${appApi}gdrive/auth/oauth2-callback?code=${encodeURIComponent(code)}&redirectUrl=${encodeURIComponent(redirectUrl)}`,
  gdriveGetToken: `${appApi}gdrive/auth/token`,
  gdriveFolders: (folderId: string) => `${appApi}gdrive/folders/${folderId}`,
  setProjectGdriveFolder: (projectId: string, folderId: string) =>
    `${appApi}projects/${projectId}/gdrive-folder?folderId=${encodeURIComponent(folderId)}`,
  syncProjectGdriveFolder: (projectId: string) => `${appApi}projects/${projectId}/sync-gdrive-folder`,
  syncProjectGdriveStream: (projectId: string) => `${appApi}projects/${projectId}/gdrive/sync-gdrive-stream`,
  gdriveSyncProgress: (projectId: string) => `${appApi}projects/${projectId}/gdrive-sync-progress`,
  saveGDriveAttachment: (projectId: string) => `${appApi}projects/${projectId}/gdrive-attachment`,

  // Payment/Stripe endpoints
  getPlans: `${appApi}payments/plans`,
  getPaymentMethods: `${appApi}payments/payment-method`,
  getPaymentMethodsUrl: (returnUrl: string) => 
    `${appApi}payments/payment-method-url?returnUrl=${encodeURIComponent(returnUrl)}`,
  // Payment/Stripe endpoints
  getInvoices: `${appApi}payments/invoices`,
  getCheckoutUrl: (planId: string, successUrl: string, cancelUrl: string) => 
    `${appApi}payments/checkout-url?planId=${encodeURIComponent(planId)}&successUrl=${encodeURIComponent(successUrl)}&cancelUrl=${encodeURIComponent(cancelUrl)}`,
  downgrade: `${appApi}payments/downgrade`,
  cancelDowngrade: `${appApi}payments/cancel-downgrade`,
  // Project bulk operations
  bulkUpdateProjects: `${appApi}projects/bulk-update`,
};

// Export base URLs for direct use
export const API_BASE_URLS = {
  app: appApi,
  backend: backendApi,
  ai: aiApi,
} as const;
